import React, { useEffect, useMemo } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Divider, Stack, Typography } from "@mui/material"

import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { StandardAlertInfo } from "../../../alert/StandardAlertInfo"
import { useDeliveryLocationToggle } from "./components/useDeliveryLocationToggle"
import { useMeetingTimeRequired } from "./components/useMeetingTimeRequired"
import { DeliveryFulfillmentType, DynamicConfigField, DynamicConfigKey, MeetingServiceConfig } from "../form/formTypes"
import { SelectedMainUserState } from "../../createOrderTypes"
import { useCreateOrderDialogType } from "../../hooks/useCreateOrderDialogType"
import { ExtraServices } from "./extraServices/ExtraServices"
import { GroupFields } from "./components/GroupFields"
import { DeferredDeliveryLocationFields } from "./components/DeferredDeliveryLocationFields"
import { DeferredMeetingTimeFields } from "./components/DeferredMeetingTimeFields"
import { IconBadge } from "../../../badges/IconBadge"
import { AccordionSection } from "./AccordionSection"

const useStyles = makeStyles()((theme) => ({
    disabledContent: {
        pointerEvents: "none",
    },
    defaultButton: {
        color: theme.palette.tailwind.blue[500],
        fontSize: "1rem",
        fontWeight: "400",
        padding: "0",
    },
    additionalFieldsContainer: {
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
        paddingTop: "1rem",
    },
    sectionTitle: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[800],
    },
    introAlert: {
        width: "100%",
    },
}))

type Props = {
    meetingServiceConfiguration: MeetingServiceConfig
    expandedAccordionKey: string | null
    setExpandedAccordionKey: (k: string | null) => void
    user: SelectedMainUserState
    merchantId?: number
}

export function DrawerSections({
    meetingServiceConfiguration,
    expandedAccordionKey,
    setExpandedAccordionKey,
    user,
    merchantId,
}: Props) {
    const { classes } = useStyles()

    const { isSpecialOrder } = useCreateOrderDialogType()

    const { sections } = meetingServiceConfiguration

    const visibleSections = (sections ?? []).filter(({ showInDrawer }) => showInDrawer !== false)

    const { deliveryFields, showOtherLocationField, setShowOtherLocationField, hasMeetingLocationRequired } =
        useDeliveryLocationToggle(visibleSections)

    const hasMeetingTimeRequired = useMeetingTimeRequired()

    const [{ value: deliveryType }] = useField<DeliveryFulfillmentType | undefined>(DynamicConfigKey.DELIVERY_TYPE)

    const deferDeliveryLocation = hasMeetingLocationRequired && deliveryType !== DeliveryFulfillmentType.DELIVERY

    const meetingTimeFields = useMemo(() => {
        if (!hasMeetingTimeRequired) return undefined
        const allFields = visibleSections.flatMap((s) => s.groups.flatMap((g) => g.fields))
        const defaultField = (key: DynamicConfigKey): DynamicConfigField => ({
            key,
            description: "",
            active: false,
            inputField: { type: "dateTime" },
        })
        return {
            startTime:
                allFields.find((f) => f.key === DynamicConfigKey.MEETING_START_TIME) ??
                defaultField(DynamicConfigKey.MEETING_START_TIME),
            endTime:
                allFields.find((f) => f.key === DynamicConfigKey.MEETING_END_TIME) ??
                defaultField(DynamicConfigKey.MEETING_END_TIME),
        }
    }, [visibleSections, hasMeetingTimeRequired])

    const DELIVERY_SECTION_KEY = "section-1"

    useEffect(() => {
        if (hasMeetingTimeRequired || hasMeetingLocationRequired) {
            setExpandedAccordionKey(DELIVERY_SECTION_KEY)
        }
    }, [hasMeetingTimeRequired, hasMeetingLocationRequired])

    return (
        <>
            {visibleSections.map(({ title, groups }, index) => {
                const responsibleGroupIndex = groups.findIndex((group) =>
                    group.fields.some((field) => field.key === DynamicConfigKey.MEETING_RESPONSIBLE),
                )
                const workgroupGroupIndex = responsibleGroupIndex === -1 ? 0 : responsibleGroupIndex

                return (
                    <AccordionSection
                        key={`section-${index}`}
                        accordionKey={`section-${index}`}
                        expandedAccordionKey={expandedAccordionKey}
                        setExpandedAccordionKey={setExpandedAccordionKey}
                        title={title}
                        updateAccordionContentUi={isSpecialOrder ? classes.disabledContent : ""}
                        icon={<IconBadge variant="numeric" color="blue" value={index + 1} />}
                    >
                        {groups.map((group, groupIndex) => {
                            const groupTitle = group.title
                            return (
                                <React.Fragment key={`section-${index}-group-${groupIndex}`}>
                                    {groupIndex > 0 && <Divider />}
                                    <GroupFields
                                        title={groupTitle}
                                        fields={group.fields}
                                        meetingServiceConfiguration={meetingServiceConfiguration}
                                        user={user}
                                        defaultButtonClass={classes.defaultButton}
                                        deliveryFields={deliveryFields}
                                        showOtherLocationField={showOtherLocationField}
                                        setShowOtherLocationField={setShowOtherLocationField}
                                        hasMeetingLocationRequired={hasMeetingLocationRequired}
                                        isSpecialOrder={isSpecialOrder}
                                        deferDeliveryLocationFields={index === 1 && deferDeliveryLocation}
                                        deferMeetingTimeFields={index === 0 && hasMeetingTimeRequired}
                                        withWorkgroup={index === 0 && groupIndex === workgroupGroupIndex}
                                    />
                                </React.Fragment>
                            )
                        })}
                        {index === 1 && (
                            <>
                                <ExtraServices userId={user.userId} merchantId={merchantId || 0} />
                                {(hasMeetingTimeRequired || deferDeliveryLocation) && (
                                    <Stack gap="1rem" className={classes.additionalFieldsContainer}>
                                        <LocalizedStrict id="create-order-extra-services-additional-fields-title">
                                            <Typography variant="body1" className={classes.sectionTitle}>
                                                Additional meeting details:
                                            </Typography>
                                        </LocalizedStrict>
                                        <LocalizedStrict
                                            id="create-order-extra-services-additional-fields-intro"
                                            attrs={{ title: true }}
                                        >
                                            <StandardAlertInfo title="" className={classes.introAlert} />
                                        </LocalizedStrict>
                                        {meetingTimeFields && (
                                            <DeferredMeetingTimeFields meetingTimeFields={meetingTimeFields} />
                                        )}
                                        {deferDeliveryLocation && (
                                            <DeferredDeliveryLocationFields
                                                user={user}
                                                deliveryFields={deliveryFields}
                                                showOtherLocationField={showOtherLocationField}
                                                setShowOtherLocationField={setShowOtherLocationField}
                                                hasMeetingLocationRequired={hasMeetingLocationRequired}
                                                defaultButtonClass={classes.defaultButton}
                                            />
                                        )}
                                    </Stack>
                                )}
                            </>
                        )}
                    </AccordionSection>
                )
            })}
        </>
    )
}
